import { type JSX, Show } from 'solid-js';
import { t } from '../../i18n';
import { controlKey, controllableJob, jobName } from '../../lib/card-controls';
import { useCardControls } from '../../lib/card-controls-context';
import type { UtilJob } from '../../lib/fleetUtilization';
import { isSaving, wantedValue } from '../../lib/flood-guard-context';
import { ControlButton } from './ControlButton';

// One job's Pause (or Resume) and Stop (#6753, prototype jobCtl, `:2140`): on a job that is
// running, starting, pausing or paused, for an admin on this org's machine only. Pause is
// offered wherever the LANE can pause (`pausable`, `isPausableTask`); whether the MACHINE can
// is its beat's verbs, which the card never sees, so the server is the per-box gate and its
// `machine_cannot_pause` / `presence_unknown` refusal shows under the job (owner ruling
// ivndev001 2026-11-01, gated per box at runtime). A job whose `pausable` is false or absent
// shows Stop only. Resume shows on a paused or pausing job (on a pausing one it withdraws
// the request). Every press goes through the page's flood guard.

export function JobControls(props: {
  readonly job: UtilJob;
  /** One job, the large layout: the buttons carry their words. */
  readonly labelled?: boolean;
}): JSX.Element {
  const handle = useCardControls();
  const shown = () => (handle?.on() === true && controllableJob(props.job) ? handle : null);
  return (
    <Show when={shown()}>
      {(h) => {
        const card = () => h().card;
        const id = () => props.job.id;
        const name = () => jobName(props.job);
        const saving = (): boolean =>
          isSaving(card().page, controlKey.jobPause(id())) ||
          isSaving(card().page, controlKey.jobStop(id())) ||
          isSaving(card().page, controlKey.jobAnywhere(id()));
        const held = (): boolean => saving() || card().locked() > 0;
        const stopWaiting = (): boolean =>
          wantedValue(card().page, controlKey.jobStop(id())) !== undefined;
        const resuming = (): boolean => card().jobValue(props.job) === 'pause';
        return (
          <span class="mission-ctl">
            <Show when={saving()}>
              <span class="mission-ctl__saving">{t('fleet.machines.ctl_saving')}</span>
            </Show>
            {/* ONE button that flips between Pause and Resume, never two: swapping the
                element would drop a keyboard user's focus to the page on every press. */}
            <Show when={resuming() || props.job.pausable === true}>
              <ControlButton
                glyph={resuming() ? 'play' : 'pause'}
                name={t(
                  resuming() ? 'fleet.machines.ctl_job_resume' : 'fleet.machines.ctl_job_pause',
                  { name: name() },
                )}
                word={t(resuming() ? 'fleet.machines.ctl_resume' : 'fleet.machines.ctl_pause')}
                labelled={props.labelled}
                held={held()}
                busy={saving()}
                onPress={() => card().toggleJob(props.job)}
              />
            </Show>
            <ControlButton
              glyph="stop"
              name={t('fleet.machines.ctl_job_stop', { name: name() })}
              word={t('fleet.machines.ctl_stop')}
              labelled={props.labelled}
              held={held() || stopWaiting()}
              busy={saving()}
              stopFor={id()}
              onPress={() => card().askStop({ kind: 'job', id: id(), name: name() })}
            />
          </span>
        );
      }}
    </Show>
  );
}
